<template>
  <div class="modal fade" id="serviceCoverageModal" tabindex="-1" data-bs-backdrop="static">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-shield me-2"></i>
            Coverage Settings - {{ service?.service_name }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" @click="resetForm"></button>
        </div>

        <div class="modal-body" v-if="service">
          <!-- Service Info -->
          <div class="alert alert-info">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ service.service_name }}</strong>
                <br>
                <small>Code: {{ service.service_code }} | Category: {{ service.service_category }}</small>
              </div>
              <div class="text-end">
                <strong>Unit Price:</strong> ETB {{ formatCurrency(service.unit_price) }}
              </div>
            </div>
          </div>

          <!-- Coverage Type -->
          <div class="row mb-3">
            <div class="col-md-6">
              <label class="form-label">Coverage Type <span class="text-danger">*</span></label>
              <select class="form-select" v-model="coverageData.coverage_type" @change="onCoverageTypeChange">
                <option value="fully_covered">Fully Covered</option>
                <option value="partially_covered">Partially Covered</option>
                <option value="excluded">Excluded</option>
              </select>
            </div>
            <div class="col-md-6" v-if="coverageData.coverage_type === 'partially_covered'">
              <label class="form-label">Patient Copayment (%)</label>
              <div class="input-group">
                <input
                    type="number"
                    class="form-control"
                    v-model="coverageData.copayment_percentage"
                    min="0"
                    max="100"
                    step="5">
                <span class="input-group-text">%</span>
              </div>
              <small class="form-text text-muted">
                Patient pays: ETB {{ calculatePatientAmount() }}
              </small>
            </div>
          </div>

          <!-- Coverage Limits -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Coverage Limits</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-4 mb-3">
                  <label class="form-label">Annual Limit (ETB)</label>
                  <div class="input-group">
                    <span class="input-group-text">ETB</span>
                    <input
                        type="number"
                        class="form-control"
                        v-model="coverageData.annual_limit"
                        min="0"
                        step="100"
                        placeholder="No limit">
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Per Visit Limit (ETB)</label>
                  <div class="input-group">
                    <span class="input-group-text">ETB</span>
                    <input
                        type="number"
                        class="form-control"
                        v-model="coverageData.per_visit_limit"
                        min="0"
                        step="10"
                        placeholder="No limit">
                  </div>
                </div>
                <div class="col-md-4 mb-3">
                  <label class="form-label">Frequency Limit</label>
                  <input
                      type="number"
                      class="form-control"
                      v-model="coverageData.frequency_limit"
                      min="0"
                      placeholder="Times per year">
                </div>
              </div>
            </div>
          </div>

          <!-- Requirements -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Requirements & Conditions</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-check mb-2">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        v-model="coverageData.preauth_required">
                    <label class="form-check-label">
                      Pre-authorization Required
                    </label>
                  </div>
                  <div v-if="coverageData.preauth_required" class="ms-4 mb-3">
                    <label class="form-label small">Pre-auth Threshold (ETB)</label>
                    <input
                        type="number"
                        class="form-control form-control-sm"
                        v-model="coverageData.preauth_threshold"
                        min="0"
                        step="100"
                        placeholder="Always required">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check mb-2">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        v-model="coverageData.requires_referral">
                    <label class="form-check-label">
                      Referral Required
                    </label>
                  </div>
                </div>
              </div>

              <!-- Age Restrictions -->
              <div class="row mt-3">
                <div class="col-md-6">
                  <label class="form-label">Age Restrictions</label>
                  <div class="input-group">
                    <input
                        type="number"
                        class="form-control"
                        v-model="coverageData.age_min"
                        min="0"
                        placeholder="Min age">
                    <span class="input-group-text">to</span>
                    <input
                        type="number"
                        class="form-control"
                        v-model="coverageData.age_max"
                        min="0"
                        placeholder="Max age">
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Gender Restriction</label>
                  <select class="form-select" v-model="coverageData.gender_restriction">
                    <option value="any">Any Gender</option>
                    <option value="male">Male Only</option>
                    <option value="female">Female Only</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Effective Dates -->
          <div class="card mb-3">
            <div class="card-header bg-light">
              <h6 class="mb-0">Effective Period</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <label class="form-label">Effective From <span class="text-danger">*</span></label>
                  <input
                      type="date"
                      class="form-control"
                      v-model="coverageData.effective_from"
                      required>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Effective To</label>
                  <input
                      type="date"
                      class="form-control"
                      v-model="coverageData.effective_to"
                      :min="coverageData.effective_from">
                </div>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="mb-3">
            <label class="form-label">Additional Notes</label>
            <textarea
                class="form-control"
                v-model="coverageData.notes"
                rows="3"
                placeholder="Special conditions, exceptions, or notes..."></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="resetForm">
            <i class="bx bx-x me-1"></i>Cancel
          </button>
          <button type="button" class="btn btn-primary" @click="saveCoverage" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-save me-1"></i>
            {{ saving ? 'Saving...' : 'Save Coverage Settings' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ServiceCoverageModal",
  props: {
    service: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      coverageData: {
        coverage_type: 'fully_covered',
        copayment_percentage: 0,
        preauth_required: false,
        preauth_threshold: null,
        annual_limit: null,
        per_visit_limit: null,
        frequency_limit: null,
        requires_referral: false,
        age_min: null,
        age_max: null,
        gender_restriction: 'any',
        effective_from: new Date().toISOString().split('T')[0],
        effective_to: null,
        notes: ''
      },
      saving: false,
      modal: null
    };
  },

  mounted() {
    this.modal = new bootstrap.Modal(document.getElementById('serviceCoverageModal'));
  },

  watch: {
    service: {
      handler(newVal) {
        if (newVal) {
          this.loadCoverageData();
        }
      },
      immediate: true
    }
  },

  methods: {
    openModal() {
      if (this.service) {
        this.loadCoverageData();
      }
      this.modal.show();
    },

    closeModal() {
      this.modal.hide();
      this.resetForm();
    },

    async loadCoverageData() {
      if (!this.service) return;

      const data = {
        "function": "getServiceCoverage",
        "serviceId": this.service.service_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success && res.data.data.coverage) {
          const coverage = res.data.data.coverage;
          this.coverageData = {
            coverage_type: coverage.coverage_type || 'fully_covered',
            copayment_percentage: coverage.copayment_percentage || 0,
            preauth_required: coverage.preauth_required == 1,
            preauth_threshold: coverage.preauth_threshold || null,
            annual_limit: coverage.annual_limit || null,
            per_visit_limit: coverage.per_visit_limit || null,
            frequency_limit: coverage.frequency_limit || null,
            requires_referral: coverage.requires_referral == 1,
            age_min: coverage.age_min || null,
            age_max: coverage.age_max || null,
            gender_restriction: coverage.gender_restriction || 'any',
            effective_from: coverage.effective_from || new Date().toISOString().split('T')[0],
            effective_to: coverage.effective_to || null,
            notes: coverage.notes || ''
          };
        }
      } catch (e) {
        console.error("Failed to load coverage data:", e);
      }
    },

    resetForm() {
      this.coverageData = {
        coverage_type: 'fully_covered',
        copayment_percentage: 0,
        preauth_required: false,
        preauth_threshold: null,
        annual_limit: null,
        per_visit_limit: null,
        frequency_limit: null,
        requires_referral: false,
        age_min: null,
        age_max: null,
        gender_restriction: 'any',
        effective_from: new Date().toISOString().split('T')[0],
        effective_to: null,
        notes: ''
      };
    },

    onCoverageTypeChange() {
      if (this.coverageData.coverage_type === 'fully_covered') {
        this.coverageData.copayment_percentage = 0;
      } else if (this.coverageData.coverage_type === 'excluded') {
        this.coverageData.copayment_percentage = 100;
      }
    },

    calculatePatientAmount() {
      if (!this.service) return 0;
      const amount = (this.service.unit_price * this.coverageData.copayment_percentage) / 100;
      return this.formatCurrency(amount);
    },

    async saveCoverage() {
      this.saving = true;

      const data = {
        "function": "updateServiceCoverage",
        "serviceId": this.service.service_id,
        "serviceCode": this.service.service_code,
        ...this.coverageData
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.$emit('coverage-updated', {
            message: 'Coverage settings updated successfully'
          });
          this.closeModal();
        } else {
          alert(res.data.message || 'Failed to update coverage settings');
        }
      } catch (e) {
        alert('An error occurred while saving coverage settings');
      } finally {
        this.saving = false;
      }
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    }
  }
};
</script>

<style scoped>
.form-check-input {
  cursor: pointer;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}
</style>